/* eslint-disable consistent-return */
/* eslint-disable no-param-reassign */
/* eslint-disable import/prefer-default-export */
import axios from 'axios';
import { createSlice } from '@reduxjs/toolkit';
import { StatusCodes } from 'http-status-codes';
import { message } from 'antd';
import { checkUrlAccess } from '../../services/auth';
import {
  loginWithEncryptedDataAsync,
  fetchUserAsync,
  logout,
} from '../../features/auth';
import theme from '../../services/theme';
import stats from '../../services/stats';
import { addStats } from '../../features/calculators';
import { useAnalyticsEventTracker } from '../tracker/Tracker';
import {
  validateEncryptedData,
  validateToken,
} from '../../utils/urlValidation';
import {
  getEncryptedToken,
  setEncryptedToken,
  removeEncryptedToken,
} from '../../services/tokenStorage';

const PROJECT_ID = process.env.REACT_APP_PROJECT_ID;

class AccessRequestUrl {
  constructor() {
    this.url = new URL(window.location);
  }

  get linkgenToken() {
    const encryptedData =
      this.url.searchParams.get('EncryptedData') ||
      this.url.searchParams.get('amp;EncryptedData');
    // Proceed with Linkgen when EncryptedData is present, regardless of key
    // Validate encrypted data to prevent XSS and injection attacks
    return validateEncryptedData(encryptedData);
  }

  get token() {
    const urlToken = this.url.searchParams.get('token');
    const validatedToken = urlToken ? validateToken(urlToken) : null;
    return validatedToken || getEncryptedToken();
  }
}

export const initialize = () => async (dispatch) => {
  const eventTracker = useAnalyticsEventTracker();
  const { isClientAccess } = checkUrlAccess();
  if (window.location.pathname.includes('//')) {
    const replacedPath =
      window.location.pathname.replace('//', '/') + window.location.search;
    window.location.replace(replacedPath);
  }

  // replace linkgen url for Aviva users
  if (
    window.location.search.includes('key=wbxPRzmK') &&
    window.location.pathname.includes('/Redir.aspx')
  ) {
    const replacedPath = `https://aviva.bicalculator.com/calculators${window.location.search}`;
    window.location.replace(replacedPath);
    return;
  }

  if (window.location.pathname.includes('/Redir.aspx')) {
    const replacedPath =
      window.location.pathname.replace('/Redir.aspx', '/calculators') +
      window.location.search;
    window.location.replace(replacedPath);
  }

  if (!isClientAccess) {
    const url = new AccessRequestUrl();

    axios.interceptors.response.use(
      (res) => res,
      async ({ response }) => {
        const status = response ? response.status : null;
        switch (status) {
          case StatusCodes.UNAUTHORIZED:
            message.destroy();
            message.error('Session expired. Please login again');
            dispatch(logout());
            return Promise.reject(response);
          default:
            return Promise.reject(response);
        }
      }
    );

    let token = url.linkgenToken;
    if (token) {
      // Ensure any old token does not interfere with Linkgen login
      removeEncryptedToken();
      delete axios.defaults.headers.common.Authorization;

      await dispatch(
        loginWithEncryptedDataAsync({ token, grantType: 'LinkgenUrl' })
      );

      // login route is used for SSO login, if linkgen url is detected then trigger Linkgen stat
      if (!window.location.pathname?.includes('/login')) {
        addStats({
          projectId: PROJECT_ID,
          typeName: stats.statLabels.linkgen,
        });
        eventTracker('Login', stats.gaLabels.linkgen);
      }
    } else {
      token = url.token;
      if (token) {
        setEncryptedToken(token);
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        await dispatch(fetchUserAsync(token));
      }
    }
  }

  theme.setSiteTheme();

  return null;
};

const initialState = { initialized: false, theme: theme.siteTheme };

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    initializeApp: (state) => {
      state.initialized = true;
    },
    setTheme: (state, { payload }) => {
      state.theme = payload;
    },
  },
});

export const { initializeApp, setTheme } = appSlice.actions;

export default appSlice.reducer;
